import React, { useState, forwardRef, useCallback } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fastLanerService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { ApkList, Shops } from './';
import { ApkItem, ShopData } from './types';
import { ShopId } from '@core/store/slices/apiSlice.ts';
import { WcnButton } from '@/components';

interface AssignmentProps {
    fastLanerOrgId: string | null;
    shopIds: ShopId[];
    onLoading?: () => void;
    onLoaded?: () => void;
}

export interface AssignmentRef {
    load: () => void;
}

const Assignment = forwardRef<AssignmentRef, AssignmentProps>(
    ({ fastLanerOrgId, shopIds, onLoading, onLoaded }, ref) => {
        const { t } = useTranslation();
        const { notifySuccess, notifyErrorResponse } = useNotifications();
        const [activating, setActivating] = useState(false);
        const [selectedApks, setSelectedApks] = useState<ApkItem[]>([]);
        const [selectedShops, setSelectedShops] = useState<ShopData[]>([]);

        const handleActivate = useCallback(async () => {
            if (!fastLanerOrgId || selectedApks.length === 0 || selectedShops.length === 0) return;

            setActivating(true);

            const apk = selectedApks[0];
            const groupIds = selectedShops.flatMap((shop) => shop.fastLanerGroupIds);

            try {
                const params = new URLSearchParams({
                    apkName: apk.packageName,
                    apkVersion: apk.version
                });
                for (const id of groupIds) {
                    params.append('groupId', id);
                }

                await fastLanerService.activateFastlanerApk(params.toString());

                notifySuccess();
                setSelectedApks([]);
                setSelectedShops([]);
            } catch (error) {
                notifyErrorResponse(error);
            } finally {
                setActivating(false);
            }
        }, [fastLanerOrgId, selectedApks, selectedShops]);

        return (
            <Box sx={{ py: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <ApkList
                            fastLanerOrgId={fastLanerOrgId}
                            selectable={true}
                            value={selectedApks}
                            onChange={setSelectedApks}
                            onLoading={onLoading}
                            onLoaded={onLoaded}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Shops
                            shopIds={shopIds}
                            selectable={true}
                            value={selectedShops}
                            onChange={setSelectedShops}
                            onLoading={onLoading}
                            onLoaded={onLoaded}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <WcnButton
                        variant="primary"
                        disabled={selectedApks.length === 0 || selectedShops.length === 0 || activating}
                        onClick={handleActivate}
                    >
                        {t('activate')}
                    </WcnButton>
                </Box>
            </Box>
        );
    }
);

Assignment.displayName = 'Assignment';

export default Assignment;
